/**
 * M218 — Smart showcase / affinity recommendations (Fases 2 + 3).
 *
 * Public surface:
 *   - bumpInteraction()        — call on any product interaction to grow the
 *                                session ε-decay counter (persisted in
 *                                sessionStorage so reloads don't reset it).
 *   - getInteractions()        — read the counter.
 *   - fetchRecommendations({context, productId, fallback, limit})
 *                              — returns `{items, source}`. Hits
 *                                /api/recommendations/me and gracefully falls
 *                                back to the diversified pool provided by the
 *                                caller (passes a function so we don't fetch
 *                                duplicate data unless we actually need it).
 *
 * The feature flag is enforced on the BACKEND; this module always calls the
 * endpoint and trusts the `flag_enabled` / `source` it returns. When the flag
 * is off the response will already be tagged "diversified" — no client-side
 * decision needed. This keeps the rollout fully server-controlled.
 */
import { api } from "./api";
import { diversifyProducts } from "./diversify";

const COUNTER_KEY = "prize_affinity_interactions";

export function getInteractions() {
  try {
    const n = parseInt(sessionStorage.getItem(COUNTER_KEY) || "0", 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function bumpInteraction() {
  try {
    const n = getInteractions() + 1;
    sessionStorage.setItem(COUNTER_KEY, String(n));
    return n;
  } catch {
    return 0;
  }
}

/**
 * Try the affinity endpoint, fall back to the local diversification pool.
 *
 * The `localPool` argument is a *function* that returns the raw product list
 * the caller already has handy (or a promise resolving to it). That way we
 * only spend the network fetch + processing when the affinity branch fails
 * or returns nothing.
 *
 * Returns: { items: Product[], source: "affinity" | "diversified" | "fallback" }
 */
export async function fetchRecommendations({
  context,           // "home" | "catalog" | "pdp_related"
  productId,         // required when context === "pdp_related"
  limit,             // server's default per context if omitted
  localPool,         // () => Product[] | Promise<Product[]> — caller's fallback data
  diversifyOpts = {},
} = {}) {
  const params = { context, interactions: getInteractions() };
  if (productId) params.product_id = productId;
  if (limit) params.limit = limit;
  try {
    const r = await api.get("/recommendations/me", { params });
    if (r?.data?.items?.length) {
      return { items: r.data.items, source: r.data.source };
    }
  } catch {
    /* fall through to local diversification */
  }
  // Backend was unavailable OR returned no items → use the caller's pool.
  const pool = typeof localPool === "function" ? await localPool() : (localPool || []);
  const arr = Array.isArray(pool) ? pool : [];
  const out = diversifyProducts(arr, { limit, ...diversifyOpts });
  return { items: out, source: "fallback" };
}
